import { Actions, ButtonLink, IconRenderer, Text } from 'braid-design-system';
import { FileSvgIcon } from '@phosphor-icons/react';
import {
  ViewSeekProfileLinkExample,
  ViewSeekProfileIconButtonExample,
  ViewSeekProfileTabExample
} from './components/ButtonExamples.tsx';
import { Recommended } from '../../../components/Recommended/Recommended';
import { Note } from '../../../components/Note/Note';

# Display candidate summary and actions

## Before you begin

Ensure that you have reviewed the [initial setup for SEEK Profiles](/use-cases/seek-profile#before-you-begin).

Review the contexts in which your software will surface SEEK Pass & SEEK Profile functionality.
Generally, there are three places to surface SEEK Profile summary information:

1. Applicant list
2. Candidate list or talent pool page
3. Candidate record or details page

The [`seekCandidateSummaries` query] accepts [browser tokens](/auth/browser-tokens) that include the `query:seek-candidate-profiles` scope.

Matching to SEEK Pass verification and SEEK Profile data is performed using the candidate’s **email address**.

## Candidate or applicant list

Conditionally display SEEK Pass and SEEK Profile elements against each candidate in the list.
The [`seekCandidateSummaries` query] lets you determine whether a given candidate has SEEK Pass verification and/or a SEEK Profile.

### Retrieve summaries

Execute the [`seekCandidateSummaries` query] once per page of candidates, not once per candidate.

<Note /> Requests will be routed to the Playground environment if all candidate
emails in `input.candidates[].email` use the reserved testing domain
`example.org`.

```graphql hirerId="seekAnzPublicTest:organization:seek:93WyyF1h" scope="query:seek-candidate-profiles query:organizations"
query ($input: SeekCandidateSummariesInput!) {
  seekCandidateSummaries(input: $input) {
    seekPrimaryEmailAddress
    seekProfile {
      exists
      images {
        profileIcon
      }
    }
    identity {
      formattedName
      verifiedIndicator

      icon {
        tooltipLabel
        images {
          light
          dark
        }
      }
    }
  }
}
```

```jsonc
{
  "input": {
    "candidates": [
      { "email": "ellaG@example.org" },
      { "email": "riley@example.org" },
      { "email": "candidate1@example.org" }
    ],
    // Required, in a 'applicant list' context.
    // In a 'candidate list' context, this may be omitted.
    "position": {
      // Required. Stable identifier of the position in your software.
      "seekPartnerPositionId": "1234567890",
      // Required. The title of the position.
      "positionTitle": "Software Engineer",
      // Required. The URI of the job ad.
      "positionUri": "https://www.partner.com/job/1234567890",
      // Optional. The ID of the SEEK job ad, if known.
      "profileId": "seekAnzPublicTest:positionProfile:jobAd:2782PZfXV",
      "positionLocation": "Sydney, Australia",
      "jobCategories": "Technology"
    }
  }
}
```

```json
{
  "seekCandidateSummaries": [
    {
      "seekPrimaryEmailAddress": "ellaG@example.org",
      "seekProfile": {
        "exists": true,
        "images": {
          "profileIcon": "https://integration.seek.com/assets/seek-profile/view-profile-icon/seek-profile.svg"
        }
      },
      "identity": {
        "formattedName": "Ella G",
        "verifiedIndicator": true,
        "icon": {
          "tooltipLabel": "Verified by SEEK Pass as Ella G",
          "images": {
            "light": "https://integration.seek.com/assets/verify-with-seek-pass/verified-icons/verified-light.svg",
            "dark": "https://integration.seek.com/assets/verify-with-seek-pass/verified-icons/verified-dark.svg"
          }
        }
      }
    },
    {
      "seekPrimaryEmailAddress": "riley@example.org",
      "seekProfile": {
        "exists": true,
        "images": {
          "profileIcon": "https://integration.seek.com/assets/seek-profile/view-profile-icon/seek-profile.svg"
        }
      },
      "identity": {
        "formattedName": null,
        "verifiedIndicator": false,
        "icon": null
      }
    },
    {
      "seekPrimaryEmailAddress": "candidate1@example.org",
      "seekProfile": {
        "exists": false,
        "images": null
      },
      "identity": {
        "formattedName": null,
        "verifiedIndicator": false,
        "icon": null
      }
    }
  ]
}
```

#### Intended use

The [`seekCandidateSummaries` query] is a client-side operation:
it is meant to run in the browser while a hirer is viewing a candidate or applicant list in your software, using a [browser token](/auth/browser-tokens) in that same user session.
Call it to decorate the UI the hirer already has open, to show candidates with a SEEK Profile.
Do not call it from a background job, batch export, or other process that processes your entire candidate database.

Your software only needs summaries for the candidates the hirer is looking at, usually a single page or screen of results.
Enumerating, harvesting, or otherwise systematically processing large candidate sets with this query is **prohibited** under the [SEEK API Terms of Use].

If your software is unable to make this query client-side, you should render a “SEEK Profile” link for all candidates in your software.
Candidates that don’t exist on SEEK will be handled gracefully in the SEEK Profile Panel.

#### Request limits

The query accepts a maximum of 100 candidate summaries per request.
If a single on-screen list has more than 100 rows, you can [make multiple requests](/graphql/in-practice#batching-limits) as a way to populate them.

### Render profile link

Use the response from the [`seekCandidateSummaries` query] to determine whether to show a profile link for each candidate:

- If `seekProfile.exists` is `true`, render a link to the [SEEK Profile Panel]
- If `seekProfile.exists` is `false`, do not render the link

#### Option 1: Text link <Recommended />

A text link may be used to represent the profile link:

<ViewSeekProfileLinkExample />

#### Option 2: Icon link

Alternatively,
you may represent the SEEK Profile with an icon button.

- Use a tooltip to explain the meaning of the icon
- The tooltip copy should be: `View SEEK Profile`
- Your software must not modify the icon

<ViewSeekProfileIconButtonExample />

### Assets

SEEK provides a bundle of SEEK Profile icon assets, if you are unable to [retrieve them via the query](#retrieve-summaries).
You can always find the latest version below.

<Actions>
  <ButtonLink
    href="https://integration.seek.com/assets/seek-profile.zip"
    icon={
      <IconRenderer>
        {({ className }) => <FileSvgIcon className={className} />}
      </IconRenderer>
    }
    download
  >
    Button assets
  </ButtonLink>
</Actions>
<Text size="small">Last updated: 2026-01-20</Text>

## Candidate details

Unconditionally display a link to the [SEEK Profile Panel] with prominent placement in the view.
For example, a tabbed view may include a dedicated SEEK Profile tab.

<ViewSeekProfileTabExample />

The SEEK Profile Panel will gracefully handle scenarios where the candidate does not have a SEEK Profile.

[SEEK Profile Panel]: /use-cases/seek-profile/panel
[SEEK API Terms of Use]: https://au.employer.seek.com/partners/terms-of-use
[`seekCandidateSummaries` query]: /schema/query/seekCandidateSummaries
